import { useState, useMemo } from 'react'

export interface SortConfig {
  key: string
  direction: 'asc' | 'desc'
}

// Função auxiliar para obter valores aninhados
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

export const useSortableTable = <T,>(data: T[]) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)

  const sortedData = useMemo(() => {
    if (!sortConfig || !data) return data

    const { key, direction } = sortConfig

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, key)
      const bValue = getNestedValue(b, key)

      // Lidar com valores nulos/undefined
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      // Comparação para diferentes tipos
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue, 'pt-BR', { sensitivity: 'base' })
        return direction === 'asc' ? comparison : -comparison
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      // Para arrays (contagem de elementos)
      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        const comparison = aValue.length - bValue.length
        return direction === 'asc' ? comparison : -comparison
      }

      // Fallback para comparação genérica
      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortConfig])

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    
    if (sortConfig && sortConfig.key === key) {
      // Se já está ordenando por esta coluna, inverter direção
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
    }
    
    setSortConfig({ key, direction })
  }

  const clearSort = () => {
    setSortConfig(null)
  }

  return {
    sortedData,
    sortConfig,
    requestSort,
    clearSort
  }
}

